import { useEffect, useState } from 'react';
import { Sidebar } from './components/Sidebar';
import { OverviewPage } from './components/Pages/OverviewPage';
import { TaskPage } from './components/Pages/TaskPage';
import { useStatus } from './state/StatusContext';
import { PinnedDropdown } from './components/PinnedDropdown';
import { OwnerNav } from './components/OwnerNav';

export default function AppShell() {
  const [activeTaskId, setActiveTaskId] = useState<string>('overview');
  const { snapshot, isStale, lastError, hasEverLoaded, secondsSinceFetch } = useStatus();

  // When items reload and the currently-selected task no longer exists, fall
  // back to overview. Source-of-truth is snapshot.items.
  useEffect(() => {
    if (activeTaskId === 'overview') return;
    const items = snapshot?.items ?? [];
    if (activeTaskId.startsWith('item-')) {
      const id = activeTaskId.slice(5);
      if (items.length > 0 && !items.some(it => it.id === id)) setActiveTaskId('overview');
    } else if (activeTaskId.startsWith('group-')) {
      const prefix = activeTaskId.slice(6);
      if (items.length > 0 && !items.some(it => {
        const m = String(it.id).match(/^([a-z][a-z0-9]*(?:-[a-z]+)*)-\d+$/i);
        return m && m[1].toLowerCase() === prefix;
      })) setActiveTaskId('overview');
    }
  }, [activeTaskId, snapshot]);

  // This dashboard is a window onto the CLI. If it cannot see the CLI's state
  // it must SAY SO, loudly, and never render zeros or a stale cache as if they
  // were the answer. A blank page that looks like "you have done no work" is
  // the worst possible output: it is indistinguishable from real data.
  const banner = (() => {
    if (lastError) {
      return {
        text: hasEverLoaded
          ? `Not live - showing the last good data (${secondsSinceFetch}s old). ${lastError}`
          : `Cannot read the worklog: ${lastError}`,
        detail: 'The numbers below are NOT from your CLI. Nothing here is authoritative until this clears.',
      };
    }
    if (!hasEverLoaded && snapshot === null) {
      return { text: 'Connecting to the worklog...', detail: 'No data has loaded yet. Any zeros below are placeholders, not your CLI state.' };
    }
    if (isStale && hasEverLoaded) {
      return { text: `Data is ${secondsSinceFetch}s stale - polling.`, detail: '' };
    }
    return null;
  })();

  return (
    <div className="layout">
      <Sidebar activeTaskId={activeTaskId} onNavigate={setActiveTaskId} />
      <main id="content">
        <OwnerNav current="/" />
        {banner && (
          <div role="alert" style={{
            margin: '0 0 16px', padding: '12px 16px', borderRadius: 8,
            border: '1px solid rgba(240,140,140,0.55)',
            background: 'rgba(60,18,18,0.72)', color: '#ffd9d9',
            font: '13px/1.5 ui-monospace, monospace',
          }}>
            <strong>{banner.text}</strong>
            {banner.detail && <div style={{ marginTop: 4, opacity: 0.85 }}>{banner.detail}</div>}
          </div>
        )}
        {activeTaskId === 'overview'
          ? <OverviewPage />
          : <TaskPage taskId={activeTaskId} />}
      </main>
      <PinnedDropdown />
    </div>
  );
}
